'use client';

import type { DailyBonusCardState, DailyBonusVariantCard } from './components/DailyBonusCard';
import type { AppearanceDailyType } from './utils/types';
import { cva } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Icon } from '@/components/entities/Sprite';
import { useCanScroll } from '@/hooks/useCanScroll';
import { useHorizontalDragScroll } from '@/hooks/useHorizontalDragScroll';
import { useIsMobile } from '@/hooks/useIsMobile';
import openModal from '@/modals/utils/openModal';
import { useAppSelector } from '@/redux/hooks';
import { cn } from '@/utils/cn';
import { GmDailyRewardsSkeleton } from '../gamification/skeletons/GmDailyRewardsSkeleton';
import DailyBonusGuestOverlay from './components/DailyBonusGuestOverlay';
import DailyBonusInformationDrawer from './components/DailyBonusInformationDrawer';
import DailyBonusTimer from './components/DailyBonusTimer';
import DailyBonusSkeleton from './components/Skeleton';
import { useDailyBonusRewards } from './hooks/useDailyBonusRewards';

const DailyBonusCard = lazy(() => import('./components/DailyBonusCard'));
interface Props {
  classNameCards?: string;
  appearance?: AppearanceDailyType;
}

const getStylesDailyBonusCard = (appearance: AppearanceDailyType) => {
  const isRewardsPage = appearance === 'rewards-page';

  return {
    variantCard:
      isRewardsPage ? 'mobile' : 'responsive' as DailyBonusVariantCard,
    amountClassName:
      isRewardsPage ? 'leading-[1.375rem] mb-1 text-lg!' : '',
    className:
      isRewardsPage ? 'h-35.5! md:w-full! w-22.5!' : '',

  };
};

const getStylesHeaderFooter = cva('p-4', {
  variants: {
    variant: {
      'side-bar': 'lg:p-2.5 p-4 pb-4 lg:pb-3',
      'rewards-page': '',
    },
  },
  defaultVariants: {
    variant: 'side-bar',
  },
});

const DailyBonus = ({ classNameCards, appearance = 'side-bar' }: Props) => {
  const t = useTranslations('daily-bonus');
  const isSideBar = appearance === 'side-bar';
  const isRewardsPage = appearance === 'rewards-page';
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const userSession = useAppSelector(state => state.user.userSession);
  const isGuest = !userSession;
  const { cards, isLoading, showTimer } = useDailyBonusRewards();

  const [isHydrated, setIsHydrated] = useState(false);

  const {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
    handleClickCapture,
    setScrollRef,
    handleDragStart,
    scrollContainerRef,
    handleScrollNext,
    handleScrollPrev,
  } = useHorizontalDragScroll();

  const canScroll = useCanScroll(scrollContainerRef.current);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const getCardsByState = (state: DailyBonusCardState) => (
    cards
      .filter(c => c.state === state)
      .sort((a, b) => a.order - b.order)
  );

  const groupedCards = useMemo(() => {
    const readyCards = getCardsByState('ready');
    const activeCards = getCardsByState('claimed');
    const lockedCards = getCardsByState('locked');

    // INFO: First list might be with ready to claim cards, after active, and last one is locked
    return [...readyCards, ...activeCards, ...lockedCards];
  }, [cards]);

  const handleOpenModal = () => {
    if (isMobile) {
      setIsOpen(true);
      return;
    }
    openModal('daily-bonus');
  };

  const skeletons = (
    isRewardsPage
      ? <GmDailyRewardsSkeleton />
      : <DailyBonusSkeleton lengthCards={8} />
  );

  if ((!isGuest && isLoading) || !isHydrated) {
    return skeletons;
  }

  return (
    <Suspense fallback={skeletons}>
      <section className="relative group/daily-wrapper rounded-2xl">
        {
          !isGuest && canScroll && (
            <>
              <button
                type="button"
                onClick={handleScrollPrev}
                className="absolute z-5 -translate-y-1/2 top-[calc(50%--0.125rem)] left-1 opacity-0 transition-opacity duration-300 group-hover/daily-wrapper:opacity-100 size-6 glass-border-card rounded-full flex justify-center items-center bg-c300/10"
              >
                <Icon name="arrow-top" className="-rotate-90 text-light size-4" />
              </button>
              <button
                type="button"
                onClick={handleScrollNext}
                className="absolute z-5 -translate-y-1/2 top-[calc(50%--0.125rem)] right-1 opacity-0 transition-opacity duration-300 group-hover/daily-wrapper:opacity-100 size-6 glass-border-card rounded-full flex justify-center items-center bg-c300/10"
              >
                <Icon name="arrow-top" className="rotate-90 text-light size-4" />
              </button>
            </>
          )
        }

        <div className={cn('relative size-full overflow-hidden rounded-[inherit] bg-daily-bonus-glass shadow-card glass-stroke-border before:z-2!', isGuest && 'bg-c900/85')}>
          {isGuest && <DailyBonusGuestOverlay />}

          <div className={cn('relative z-1', isGuest && 'bg-c900/85')}>
            <div className={cn('flex items-center md:gap-1', getStylesHeaderFooter({ variant: appearance }))}>
              <h3 className="flex-1 text-white leading-5 text-sm font-semibold">{t('daily-reward')}</h3>
              {showTimer && <DailyBonusTimer />}
            </div>

            <div
              ref={setScrollRef}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerCancel}
              onClickCapture={handleClickCapture}
              onDragStart={handleDragStart}
              className={cn('overflow-x-auto overflow-y-hidden hide-scrollbar select-none -mt-12 -mb-13 lg:-mb-9.5 pt-12 pb-13 lg:pb-9.5 px-4 -mx-4', canScroll && 'cursor-grab active:cursor-grabbing')}
            >
              <div className={cn('flex relative gap-2 lg:gap-1.5 lg:px-2.5 px-4 min-w-max', isRewardsPage && 'px-4! gap-2!', classNameCards)}>
                {groupedCards.map(({ id, order, ...cardProps }) => (
                  <DailyBonusCard
                    key={id}
                    index={order}
                    {...getStylesDailyBonusCard(appearance)}
                    {...cardProps}
                  />
                ))}
              </div>
            </div>
            {
              isSideBar && (
                <div className="lg:block hidden h-35 w-8.5 absolute top-6.5 right-0 bg-daily-bonus-gradient-side" />
              )
            }

            <div className={cn('flex relative z-10 justify-center', getStylesHeaderFooter({ variant: appearance }))}>
              <button
                type="button"
                className="group flex items-center gap-1 font-normal text-xs leading-4 text-c300 transition-colors duration-300 md:hover:cursor-pointer md:hover:text-white"
                onClick={handleOpenModal}
              >
                {t('rewards-stack-learn-more')}
                <Icon name="info-payment" className="size-3 text-c300 transition-colors duration-300 md:group-hover:text-white" />
              </button>
            </div>
          </div>

          <DailyBonusInformationDrawer isOpen={isOpen} setIsOpen={setIsOpen} />
        </div>
      </section>
    </Suspense>
  );
};

export default DailyBonus;
